import React, { useState, useEffect } from "react";
import "./Login.css";
import "../styles/shared-animations.css";
import { loginUser } from "../components/api";
import useTranslation from "../hooks/useTranslation";
import GlassInputWrapper from "../components/GlassInputWrapper";
import PasswordInput from "../components/PasswordInput";
import XiAtmosphere from "../components/ui/XiAtmosphere";

export default function LoginModal({
  open,
  onClose,
  onLoginSuccess,
  redirectMsg,
  initialEmail,
}) {
  const [role, setRole] = useState("");
  const [form, setForm] = useState({
    name: "",
    password: "",
  });
  const [message, setMessage] = useState("");
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotEmail, setForgotEmail] = useState("");
  const [forgotMessage, setForgotMessage] = useState("");
  const [forgotLoading, setForgotLoading] = useState(false);
  const { t } = useTranslation();

  useEffect(() => {
    if (!open) return;
    const storedRole = localStorage.getItem("neuro_selected_role");
    if (storedRole) {
      setRole(storedRole);
    }
    setForm({ name: initialEmail || "", password: "" });
    setMessage("");
  }, [open, initialEmail]);

  useEffect(() => {
    if (!open) return;
    function handleKey(e) {
      if (e.key === "Escape") onClose();
    }
    function handleClick(e) {
      if (e.target.classList.contains("login-modal-overlay")) onClose();
    }
    if (typeof window !== "undefined") {
      window.addEventListener("keydown", handleKey);
      document.addEventListener("mousedown", handleClick);
      return () => {
        window.removeEventListener("keydown", handleKey);
        document.removeEventListener("mousedown", handleClick);
      };
    }
  }, [open, onClose]);

  function notifyLoginSuccess() {
    setTimeout(() => {
      try {
        if (onLoginSuccess) onLoginSuccess();
      } catch {}
    }, 1500);
  }

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const res = await loginUser({
        name: form.name,
        password: form.password,
      });

      if (res.user_id) {
        localStorage.setItem("neuro_user_id", res.user_id);
        localStorage.setItem("neuro_user_name", res.username);
        localStorage.removeItem("neuro_selected_role");

        if (res.access_token) {
          localStorage.setItem("neuro_access_token", res.access_token);
        }
        if (res.refresh_token) {
          localStorage.setItem("neuro_refresh_token", res.refresh_token);
        }

        if (res.role) {
          localStorage.setItem("neuro_user_role", res.role);
        }

        if (res.is_hospital_doctor !== undefined) {
          localStorage.setItem(
            "neuro_is_hospital_doctor",
            res.is_hospital_doctor ? "true" : "false",
          );
        }

        if (res.user_details) {
          try {
            localStorage.setItem(
              "neuro_user_info",
              JSON.stringify(res.user_details.info || null),
            );
          } catch (e) {
            console.error("Error storing user info:", e);
          }
        }

        try {
          const base =
            process.env.REACT_APP_API_URL ||
            (typeof window !== "undefined" &&
            (window.location.hostname === "localhost" ||
              window.location.hostname === "127.0.0.1")
              ? "http://localhost:8000"
              : "https://app.neurolonic.com/api");
          const userDetailsRes = await fetch(
            `${base}/user/details?user_id=${res.user_id}`,
            { headers: { Authorization: `Bearer ${res.access_token || ''}` } },
          );
          const userDetailsData = await userDetailsRes.json();

          if (
            userDetailsData &&
            userDetailsData.status_code === 200 &&
            userDetailsData.data
          ) {
            const userRole = userDetailsData.data.role || res.role;
            localStorage.setItem("neuro_user_role", userRole);

            if (userDetailsData.data.info) {
              try {
                const info =
                  typeof userDetailsData.data.info === "string"
                    ? JSON.parse(userDetailsData.data.info)
                    : userDetailsData.data.info;
                if (info && info.hospital_id) {
                  localStorage.setItem("neuro_is_hospital_doctor", "true");
                  localStorage.setItem(
                    "neuro_user_info",
                    JSON.stringify(userDetailsData.data.info),
                  );
                } else {
                  localStorage.setItem("neuro_is_hospital_doctor", "false");
                }
              } catch (e) {
                console.error("Error parsing user info:", e);
              }
            }

            setMessage(res.message + (res.username ? ", " + res.username : ""));
            setRole(userRole);

            notifyLoginSuccess();
          } else {
            setMessage(res.message + (res.username ? ", " + res.username : ""));
            notifyLoginSuccess();
          }
        } catch (userDetailsError) {
          console.error("Failed to fetch user details:", userDetailsError);
          setMessage(res.message + (res.username ? ", " + res.username : ""));
          notifyLoginSuccess();
        }
      } else {
        setMessage(res.message || "Login failed");
      }
    } catch (err) {
      setMessage(
        err.response?.data?.message || "Login failed. Please try again.",
      );
      setForm((prev) => ({ ...prev, password: "" }));
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setForgotLoading(true);
    setForgotMessage("");

    try {
      const base =
        process.env.REACT_APP_API_URL ||
        (typeof window !== "undefined" &&
        (window.location.hostname === "localhost" ||
          window.location.hostname === "127.0.0.1")
          ? "http://localhost:8000"
          : "https://app.neurolonic.com/api");
      const response = await fetch(
        `${base}/mobile/reset-password/generate-link`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ email: forgotEmail }),
        },
      );
      const data = await response.json();

      if (response.ok) {
        setForgotMessage(
          "Password reset link has been sent to your email. Please check your inbox.",
        );
        setForgotEmail("");
        setTimeout(() => setShowForgotPassword(false), 3000);
      } else {
        setForgotMessage(
          data.message || "Failed to send reset link. Please try again.",
        );
      }
    } catch (err) {
      setForgotMessage("Network error. Please try again.");
    } finally {
      setForgotLoading(false);
    }
  };

  if (!open) return null;

  return (
    <div className="login-modal-overlay">
      <div className="login-modal-container">
        {/* Left column: sign-in form */}
        <section className="login-modal-left">
          <button
            type="button"
            className="login-close-btn"
            aria-label={t("Close login modal")}
            onClick={onClose}
          >
            ×
          </button>

          <div className="login-modal-content">
            <h1 className="login-modal-title animate-element animate-delay-100">
              {t("Welcome to Xi")}
            </h1>

            {redirectMsg && (
              <div className="login-redirect-msg animate-element animate-delay-200">
                {redirectMsg}
              </div>
            )}

            <p className="login-modal-description animate-element animate-delay-200">
              {t("Access your account and continue your journey with us")}
            </p>

            <form className="login-modal-form" onSubmit={handleSubmit}>
              {role && (
                <div className="login-selected-role animate-element animate-delay-300">
                  {t(role)}
                </div>
              )}

              <div className="form-group-modal animate-element animate-delay-400">
                <label className="form-label-modal">
                  {t("Email or Username")}
                </label>
                <GlassInputWrapper>
                  <input
                    name="name"
                    type="text"
                    autoComplete="username"
                    required
                    value={form.name}
                    onChange={handleChange}
                    placeholder={t("Enter email or username")}
                    className="form-input-modal"
                  />
                </GlassInputWrapper>
              </div>

              <div className="form-group-modal animate-element animate-delay-500">
                <label className="form-label-modal">{t("Password")}</label>
                <GlassInputWrapper>
                  <PasswordInput
                    name="password"
                    required
                    value={form.password}
                    onChange={handleChange}
                    placeholder={t("Enter password")}
                  />
                </GlassInputWrapper>
              </div>

              {message && (
                <div
                  className={`login-message animate-element ${message.includes("successful") ? "success" : "error"}`}
                >
                  {message}
                </div>
              )}

              <button
                className="login-btn-modal submit animate-element animate-delay-600"
                type="submit"
              >
                {t("Login")}
              </button>

              <div className="login-signup-modal animate-element animate-delay-700">
                {t("Don't have an account?")}{" "}
                <button
                  type="button"
                  className="login-signup-link"
                  onClick={() => {
                    if (typeof window !== "undefined")
                      window.location.href = "/getstarted";
                  }}
                >
                  {t("Sign up now")}
                </button>
              </div>

              <div className="login-forgot-password animate-element animate-delay-700">
                <button
                  type="button"
                  className="forgot-password-link"
                  onClick={() => setShowForgotPassword(true)}
                >
                  {t("Forgot Password?")}
                </button>
              </div>
            </form>
          </div>
        </section>

        {showForgotPassword && (
          <div
            className="forgot-password-modal-overlay"
            onClick={() => setShowForgotPassword(false)}
          >
            <div
              className="forgot-password-modal"
              onClick={(e) => e.stopPropagation()}
            >
              <button
                type="button"
                className="forgot-password-close-btn"
                onClick={() => setShowForgotPassword(false)}
              >
                ×
              </button>
              <h2 className="forgot-password-title">
                {t("Reset Your Password")}
              </h2>
              <p className="forgot-password-description">
                {t(
                  "Enter your email address and we will send you a link to reset your password.",
                )}
              </p>
              <form
                className="forgot-password-form"
                onSubmit={handleForgotPassword}
              >
                <div className="form-group-modal">
                  <label className="form-label-modal">
                    {t("Email Address")}
                  </label>
                  <GlassInputWrapper>
                    <input
                      type="email"
                      value={forgotEmail}
                      onChange={(e) => setForgotEmail(e.target.value)}
                      placeholder={t("Enter your email")}
                      className="form-input-modal"
                      required
                    />
                  </GlassInputWrapper>
                </div>
                {forgotMessage && (
                  <div
                    className={`forgot-password-message ${forgotMessage.includes("sent") || forgotMessage.includes("Sent") ? "success" : "error"}`}
                  >
                    {forgotMessage}
                  </div>
                )}
                <button
                  type="submit"
                  className="forgot-password-btn"
                  disabled={forgotLoading}
                >
                  {forgotLoading ? t("Sending...") : t("Send Reset Link")}
                </button>
              </form>
            </div>
          </div>
        )}

        {/* Right column: Xi cinematic atmosphere (local, no external assets) */}
        <section className="login-modal-right" aria-hidden="true">
          <div className="login-modal-hero-image animate-slide-right animate-delay-300">
            <XiAtmosphere variant="auth" />
          </div>
        </section>
      </div>
    </div>
  );
}
